// Shared pill primitive. The single source for the consistent size, weight,
// and contrast of the small rounded labels and quick-filter chips used across
// Cockpit. Status pills (live/progress/planned) keep their own StatusPill;
// this covers neutral labels and selectable filter chips.
//
// Two variants:
//   outline  (default) - transparent fill, line border, ink text. The resting
//                         look for chips and neutral labels.
//   selected           - filled dark (olive-deep) with cream text. The active
//                         look for a quick-filter chip. Stays high contrast in
//                         BOTH light and dark mode (cream on a dark fill), which
//                         fixes the old white-on-mid-olive "Pilot 16" chip that
//                         sat at ~2.8:1.
//
// Tinted (semantic) pill: pass a `tone` string (background + text color
// classes, e.g. STATUS_TONES[status]) to render a status / ship / health /
// stage pill in the shared shape, size, padding, and weight while keeping the
// meaning its color carries. `tone` takes precedence over `variant`. This keeps
// every pill in one family: the base owns shape/size/weight, the tone owns
// color. See COCKPIT.md section 2.9.
//
// Renders as a <span> by default, or a <button> when onClick is supplied.
//
// Class strings live in pillClasses.ts so they can be shared / unit-tested
// without tripping react-refresh's only-export-components rule.

import type { ReactNode } from 'react';
import {
  PILL_BASE,
  PILL_VARIANT,
  tintedPillClass,
  type PillVariant,
} from './pillClasses';

export type { PillVariant };

type Props = {
  children: ReactNode;
  variant?: PillVariant;
  // Semantic color classes (bg + text, light + dark) layered over the shared
  // base. When set, `variant` is ignored. The tone carries the meaning; the
  // base fixes the shape/size/weight so it matches every other pill.
  tone?: string;
  onClick?: () => void;
  className?: string;
  title?: string;
  'aria-pressed'?: boolean;
};

export function Pill({
  children,
  variant = 'outline',
  tone,
  onClick,
  className,
  title,
  ...rest
}: Props) {
  const cls = tone
    ? tintedPillClass(tone, className)
    : `${PILL_BASE} ${PILL_VARIANT[variant]}${className ? ` ${className}` : ''}`;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cls}
        title={title}
        aria-pressed={rest['aria-pressed']}
      >
        {children}
      </button>
    );
  }
  return (
    <span className={cls} title={title}>
      {children}
    </span>
  );
}
